import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { keepPreviousData, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import chunk from 'lodash/chunk';
import isNil from 'lodash/isNil';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { useFilterState } from '~/state/tableFilters';
import { useSorting } from '~/state/tableSorting';
import { Company } from '~/types/company';
import { DocumentQueryKeys } from '~/types/document';
import { Flow, FlowsQueryKeys } from '~/types/flow';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { OutstandingKeys } from '~/types/outstanding';
import {
  Reconciliation,
  ReconciliationRelationStatus,
  ReconciliationsFiltersFields,
  ReconciliationsMeta,
  ReconciliationSource,
  ReconciliationsQueryFilters,
  ReconciliationsQueryKeys,
  ReconciliationsQuerySorting,
} from '~/types/reconciliation';
import { Transaction, TransactionsKeys } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { keyBy } from '~/utils/array';
import { isNotEmpty } from '~/utils/helpers';
import {
  buildJsonAPIQueryParams,
  IJsonAPIFilterBuilder,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  SortInfo,
} from '~/utils/jsonapi-query';
import { AnalyticsEvents } from './analytics';
import { backendApiGateway } from '../client/backend';

const ReconciliationsPageSize = 50;

const emptyReconciliationsQueryFilters: ReconciliationsQueryFilters = {
  createdAt: [null, null],
  sources: [],
};

const initialReconciliationsQuerySorting: SortInfo<ReconciliationsQuerySorting> = {
  sort: 'createdAt',
  direction: 'desc',
};

const fetchReconciliationsByIds = async (
  ids: Reconciliation['id'][],
  includes: string[],
  filters: ReconciliationsQueryFilters = emptyReconciliationsQueryFilters,
): Promise<Reconciliation[]> => {
  const includeBuilder = jsonAPIIncludeBuilder(includes);
  const chunks = chunk(ids, 25);

  const reconciliationsChunks = await Promise.all(
    chunks.map(async (ids) => {
      const filterBuilder = reconciliationsFiltersToJsonApiFilters(filters);
      filterBuilder.in('id', ids);

      const params = buildJsonAPIQueryParams(filterBuilder, includeBuilder);
      const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/reconciliations/', {
        params,
      });
      return deserialize<Reconciliation>(data) as Reconciliation[];
    }),
  );

  return reconciliationsChunks.flatMap((el) => el);
};

export const decorateWithReconciliations = async <T extends { reconciliations?: Reconciliation[] }>(
  resources: T[],
  includes:
    | ['flow', 'flow.bookkeepingEntry', 'flow.document', 'flow.document.counterpart']
    | ['transaction']
    | ['transaction', 'transaction.bookkeepingTransaction'],
  filters?: ReconciliationsQueryFilters,
): Promise<T[]> => {
  const reconciliationIds = resources.flatMap((el) => el.reconciliations?.map((el) => el.id) ?? []);

  const allReconciliations = await fetchReconciliationsByIds(reconciliationIds, includes, filters);

  const reconciliationsDict = keyBy(allReconciliations, 'id');
  return resources.map((r) => {
    const reconciliations = (r.reconciliations ?? []).map((r) => reconciliationsDict[r.id]).filter(isDefined);
    return {
      ...r,
      reconciliations,
    };
  });
};

const createReconciliation = async ({
  companyId,
  flowId,
  transactionId,
  source,
  status,
}: {
  companyId: Company['id'];
  transactionId: Transaction['id'];
  flowId: Flow['id'];
  source: ReconciliationSource;
  status: ReconciliationRelationStatus;
}) => {
  const payload = serialize(
    {
      status,
      source,
      company: { id: companyId },
      transaction: { id: transactionId },
      flow: { id: flowId },
    },
    'reconciliation',
    {
      relationships: ['company', 'flow', 'transaction'],
    },
  );
  const { data } = await backendApiGateway.post<void>('/api/v1/reconciliations', payload);
  return data;
};

const deleteReconciliation = async ({
  reconciliationId,
}: {
  reconciliationId: Reconciliation['id'];
  context: string;
}) => {
  const { data } = await backendApiGateway.delete<void>(`/api/v1/reconciliations/${reconciliationId}`);
  return data;
};

export const invalidateReconciliationCaches = () => {
  queryClient.invalidateQueries({ queryKey: ReconciliationsQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
  queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: OutstandingKeys.all });
};

interface CreateReconciliationVariables {
  transactionId: Transaction['id'];
  flowId: Flow['id'];
  source: ReconciliationSource;
  context: string;
}

export const useCreateReconciliationMutation = (options?: { skipCacheInvalidation: boolean }) => {
  const { track } = useAnalytics();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: ({ flowId, transactionId, source }: CreateReconciliationVariables) =>
      createReconciliation({
        companyId,
        flowId,
        transactionId,
        source,
        status: ReconciliationRelationStatus.VERIFIED,
      }),
    onSuccess: (_data, { context, source }) => {
      if (!options?.skipCacheInvalidation) invalidateReconciliationCaches();
      switch (source) {
        case ReconciliationSource.MANUAL:
          track(AnalyticsEvents.RECONCILIATION_CREATED, { context });
          break;
        case ReconciliationSource.PROPOSAL:
          track(AnalyticsEvents.RECONCILIATION_MATCHED, { context });
          break;
      }
    },
  });
};

export const useRejectReconciliationMutation = (options?: { skipCacheInvalidation: boolean }) => {
  const { track } = useAnalytics();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: ({ flowId, transactionId }: Omit<CreateReconciliationVariables, 'source'>) =>
      createReconciliation({
        companyId,
        flowId,
        transactionId,
        source: ReconciliationSource.PROPOSAL,
        status: ReconciliationRelationStatus.REJECTED,
      }),
    onSuccess: (_data, { context }) => {
      if (!options?.skipCacheInvalidation) invalidateReconciliationCaches();
      track(AnalyticsEvents.RECONCILIATION_REJECTED, { context });
    },
  });
};

export const useDeleteReconciliationMutation = (options?: { skipCacheInvalidation: boolean }) => {
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: deleteReconciliation,
    onSuccess: (_data, { context }) => {
      track(AnalyticsEvents.RECONCILIATION_DELETED, { context });
    },
    onSettled: () => {
      if (!options?.skipCacheInvalidation) invalidateReconciliationCaches();
    },
  });
};

const useReconciliationsTableQueryFilters = (): ReconciliationsQueryFilters => {
  const filters = useFilterState<ReconciliationsFiltersFields>();
  return {
    createdAt: filters.dateRange,
    sources: filters.sources,
  };
};

const reconciliationsFiltersToJsonApiFilters = (filters: ReconciliationsQueryFilters): IJsonAPIFilterBuilder => {
  const builder = jsonAPIFilterBuilder();

  if (!isNil(filters.createdAt) && !isNil(filters.createdAt[0])) {
    const createdAt = filters.createdAt[0];
    builder.gte('createdAt', createdAt);
  }
  if (!isNil(filters.createdAt) && !isNil(filters.createdAt[1])) {
    const createdAt = filters.createdAt[1];
    builder.lt('createdAt', createdAt);
  }
  if (isNotEmpty(filters.sources)) builder.in('source', filters.sources);

  if (isNotEmpty(filters.statuses)) {
    builder.in('status', filters.statuses);
  }

  return builder;
};

const fetchReconciliations = async (
  companyId: Company['id'],
  filters: ReconciliationsQueryFilters = emptyReconciliationsQueryFilters,
  options: {
    size: number;
    cursor?: string;
    sorting: SortInfo<ReconciliationsQuerySorting>;
    include?: string[];
  } = { size: ReconciliationsPageSize, sorting: initialReconciliationsQuerySorting },
) => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'flow',
    'flow.bookkeepingEntry',
    'flow.document',
    'flow.document.counterpart',
    'transaction',
    'transaction.account',
    'transaction.account.consent',
    'transaction.account.consent.institution',
    'transaction.bookkeepingTransaction',
  ]);

  const filterBuilder = reconciliationsFiltersToJsonApiFilters(filters);
  filterBuilder.eq('company.id', companyId);
  filterBuilder.eq('status', ReconciliationRelationStatus.VERIFIED);

  const sortingBuilder = jsonAPISortBuilder([
    {
      sort: options.sorting.sort,
      direction: options.sorting.direction,
    },
    {
      sort: 'id',
      direction: options.sorting.direction,
    },
  ]);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options.size);
  if (options.cursor) paginationParamsBuilder.cursor(options.cursor);

  const searchParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, sortingBuilder, paginationParamsBuilder);

  const { data } = await backendApiGateway.get<DocumentObject & { meta: ReconciliationsMeta }>(
    '/api/v1/reconciliations',
    {
      params: searchParams,
    },
  );

  return {
    reconciliations: deserialize<Reconciliation>(data) as Reconciliation[],
    page: (data.meta as ReconciliationsMeta).page,
  };
};

export const useReconciliationsTableInfiniteQuery = () => {
  const companyId = useActiveCompanyId();

  const filters = useReconciliationsTableQueryFilters();
  const sorting = useSorting<ReconciliationsQuerySorting>();

  return useInfiniteQuery({
    queryKey: ReconciliationsQueryKeys.companyInfinityList(companyId, filters, sorting),
    queryFn: ({ pageParam }) =>
      fetchReconciliations(companyId, filters, {
        size: ReconciliationsPageSize,
        cursor: pageParam,
        sorting,
      }),
    initialPageParam: '',
    // Mutations invalidate this cache explicitly, so a stale window avoids
    // refetching every loaded page on each remount/refocus.
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    getNextPageParam: ({
      page: { cursor },
      reconciliations,
    }: {
      page: MetaDataCursorPage;
      reconciliations: Reconciliation[];
    }) => {
      if (reconciliations.length >= ReconciliationsPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !!companyId,
  });
};
